import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Loader2, Repeat, ShieldCheck } from "lucide-react";
import { launchCheckout } from "@/lib/billing";

const TIER_LABELS = {
  starter: "Starter", growth: "Growth", regional: "Regional",
  enterprise: "Enterprise", statewide: "Statewide", individual: "Individual",
};

export default function Billing() {
  const [state, setState] = useState({ user_subscription: null, organization_subscription: null });
  const [txs, setTxs] = useState([]);
  const [subscribing, setSubscribing] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/transactions"),
      ]);
      setState(s.data);
      setTxs(t.data.transactions || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (pkg) => {
    setSubscribing(pkg);
    try {
      const { data } = await api.post("/billing/subscribe", {
        package_id: pkg, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subscription failed to start");
    } finally { setSubscribing(""); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal unavailable");
    } finally { setPortalLoading(false); }
  };

  const active = state.user_subscription?.status === "active" || state.organization_subscription?.status === "active";
  const currentTier = state.organization_subscription?.tier || state.user_subscription?.tier;

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">Billing & Subscription</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Manage your plan.</h1>
        <p className="mt-2 text-sm text-stone-600">Recurring annual subscription with self-service management via the Stripe Customer Portal.</p>
      </div>

      {/* Current subscription */}
      <div className="card-flat">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-mono">Current Subscription</div>
            <div className="mt-2 flex items-center gap-3">
              <div className={`h-2.5 w-2.5 rounded-full ${active ? "bg-emerald-500" : "bg-stone-300"}`} />
              <div className="font-display text-2xl font-medium text-slate-950">
                {active ? (TIER_LABELS[currentTier] || "Active") : "No active subscription"}
              </div>
            </div>
            {state.organization_subscription?.current_period_end && (
              <div className="mt-2 font-mono text-xs text-stone-500">
                Renews {new Date(state.organization_subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
            {state.user_subscription?.current_period_end && !state.organization_subscription && (
              <div className="mt-2 font-mono text-xs text-stone-500">
                Renews {new Date(state.user_subscription.current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
          <button data-testid="billing-portal-btn" onClick={openPortal} disabled={portalLoading || !state.user_subscription?.stripe_customer_id} className="btn-secondary">
            {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            Manage in Portal
          </button>
        </div>
      </div>

      {/* Subscribe options */}
      <div>
        <div className="label-mono mb-3">Recurring Plans (Annual Auto-Renewal)</div>
        <div className="grid gap-4 md:grid-cols-3">
          <PlanCard title="Entrepreneur" price="$195" period="/year" desc="Solo founder — full platform access." pkg="entrepreneur_annual" onSubscribe={subscribe} loading={subscribing === "entrepreneur_annual"} />
          <PlanCard title="Growth (Org)" price="$12,500" period="/year" desc="100 seats. Most popular org tier." pkg="org_growth" onSubscribe={subscribe} loading={subscribing === "org_growth"} featured />
          <PlanCard title="Enterprise (Org)" price="$50,000" period="/year" desc="1,000 seats. For state programs & major foundations." pkg="org_enterprise" onSubscribe={subscribe} loading={subscribing === "org_enterprise"} />
        </div>
      </div>

      {/* One-time / add-ons */}
      <div>
        <div className="label-mono mb-3">Add-ons & One-time</div>
        <div className="grid gap-3 md:grid-cols-4">
          {[
            ["addon_capital_raising", "AI Capital Raising System™", "$4,995/yr"],
            ["addon_pitch_suite", "AI Pitch Competition Suite", "$5,000/yr"],
            ["addon_judge_assistant", "AI Judge Assistant", "$3,000/yr"],
            ["implementation", "Implementation & Onboarding", "$5,000 one-time"],
          ].map(([pkg, label, price]) => (
            <div key={pkg} className="rounded-md border border-stone-200 bg-white p-4">
              <div className="text-sm font-medium text-slate-950">{label}</div>
              <div className="mt-1 font-mono text-xs text-emerald-600">{price}</div>
              <button
                onClick={() => launchCheckout(pkg)}
                data-testid={`buy-${pkg}`}
                className="btn-secondary mt-3 w-full !text-xs !px-3 !py-2"
              >
                Purchase
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Transactions */}
      <div className="card-flat">
        <div className="label-mono mb-3">Transaction History</div>
        <div className="overflow-x-auto rounded-md border border-stone-200">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left">
              <tr className="border-b border-stone-200">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Date</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Package</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Amount</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Status</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Mode</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {txs.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{new Date(t.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-slate-950">{t.package_id}</td>
                  <td className="px-4 py-3 font-mono text-slate-950">${(t.amount || 0).toLocaleString()}</td>
                  <td className={`px-4 py-3 font-mono text-xs uppercase ${t.payment_status === "paid" ? "text-emerald-700" : "text-stone-500"}`}>{t.payment_status || t.status}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">{t.mode || "one_time"}</td>
                </tr>
              ))}
              {txs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-xs text-stone-500">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ title, price, period, desc, pkg, onSubscribe, loading, featured }) {
  return (
    <div className={`rounded-lg border p-6 ${featured ? "border-slate-950 bg-white shadow-[0_20px_60px_-15px_rgb(15,23,42,0.15)]" : "border-stone-200 bg-white"}`}>
      {featured && <div className="label-mono !text-[9px] !text-emerald-600 mb-2">Most Popular</div>}
      <div className="flex items-center gap-2">
        <Repeat className="h-4 w-4 text-slate-950" />
        <div className="font-display text-lg font-medium text-slate-950">{title}</div>
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <div className="font-display text-3xl font-bold text-slate-950">{price}</div>
        <div className="font-mono text-xs text-stone-500">{period}</div>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-stone-600">{desc}</p>
      <div className="mt-2 flex items-center gap-1 font-mono text-[10px] text-stone-500">
        <ShieldCheck className="h-3 w-3 text-emerald-500" /> Auto-renew • Cancel anytime
      </div>
      <button onClick={() => onSubscribe(pkg)} disabled={loading} data-testid={`subscribe-${pkg}`} className={`${featured ? "btn-primary" : "btn-secondary"} mt-6 w-full !text-xs`}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
        Subscribe
      </button>
    </div>
  );
}
